import { auth } from '@/firebase/config';
import { BEARER_PREFIX } from '@/lib/api/constants';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

async function getAuthToken(): Promise<string | null> {
  const currentUser = auth().currentUser;
  if (!currentUser) {
    return null;
  }
  return currentUser.getIdToken();
}

// Query params can be strings, numbers, or booleans
export type QueryParams = Record<string, string | number | boolean | undefined>;

class ApiClient {
  private baseUrl = '/api';

  private async request<T>(
    method: string,
    path: string,
    options?: {
      params?: QueryParams;
      body?: unknown;
    }
  ): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);

    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const token = await getAuthToken();
    if (!token) {
      throw new ApiError(401, 'Not authenticated');
    }

    const headers: HeadersInit = {
      'Authorization': `${BEARER_PREFIX}${token}`,
    };

    if (options?.body) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(url.toString(), {
      method,
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new ApiError(res.status, res.statusText, data);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return res.json();
  }

  get<T>(path: string, params?: QueryParams): Promise<T> {
    return this.request<T>('GET', path, { params });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, { body });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, { body });
  }

  delete<T>(path: string, params?: QueryParams): Promise<T> {
    return this.request<T>('DELETE', path, { params });
  }
}

export const apiClient = new ApiClient();
